import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { scenes, sceneOrder, exteriorModels, sheens as SHEENS } from "../config/site.config";

const PaintContext = createContext(null);
const KEY = "sab-colours-v2";
export const MAX_FAVS = 8;

const initialColors = () => Object.fromEntries(sceneOrder.map((id) => [id, { ...scenes[id].original }]));
const defaultSheens = (id) => Object.fromEntries(Object.entries(scenes[id].surfaces).map(([k, s]) => [k, s.sheen || "eggshell"]));
const initialSheens = () => {
  const base = Object.fromEntries(sceneOrder.map((id) => [id, defaultSheens(id)]));
  try {
    const saved = JSON.parse(localStorage.getItem("sab-sheens-v1") || "{}");
    sceneOrder.forEach((id) => Object.assign(base[id], saved[id] || {}));
  } catch {
    /* ignore */
  }
  return base;
};
export const sheenOf = (id) => SHEENS.find((s) => s.id === id) || SHEENS[1];

// Group targets come from config; "piece:<surface>" targets a single part.
export const resolveTarget = (sceneId, id) => {
  const cfg = scenes[sceneId];
  if (id?.startsWith("piece:")) {
    const s = id.slice(6);
    const sc = cfg.surfaces[s];
    if (sc) return { id, label: sc.label, surfaces: [s], piece: true, help: "Just this one piece. Tap another part of the picture, or use the list, to choose a different piece.", groups: sc.groups || cfg.targets[0].groups };
  }
  return cfg.targets.find((t) => t.id === id) || cfg.targets[0];
};

const loadColors = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!saved) return initialColors();
    const base = initialColors();
    sceneOrder.forEach((id) => Object.assign(base[id], saved[id] || {}));
    return base;
  } catch {
    return initialColors();
  }
};

// Older saves used UK spelling ("Original colour", "favourite"); always show US spelling.
const usSpelling = (v) => (typeof v === "string" ? v.replace(/colour/g, "color").replace(/Colour/g, "Color").replace(/COLOUR/g, "COLOR").replace(/avourite/g, "avorite") : v);

export const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export const PaintProvider = ({ children }) => {
  const [colors, setColors] = useState(loadColors);
  const [labels, setLabels] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("sab-labels-v1") || "{}");
      return Object.fromEntries(Object.entries(saved).map(([k, v]) => [k, usSpelling(v)]));
    } catch {
      return {};
    }
  });
  const [scene, setScene] = useState("living");
  const [targets, setTargets] = useState(() => Object.fromEntries(sceneOrder.map((id) => [id, scenes[id].targets[0].id])));
  const [sheenState, setSheenState] = useState(initialSheens);
  const [lastExterior, setLastExterior] = useState("exterior");
  const [compare, setCompare] = useState(false);
  const [transition, setTransition] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(colors));
      localStorage.setItem("sab-labels-v1", JSON.stringify(labels));
    } catch {
      /* storage unavailable */
    }
  }, [colors, labels]);

  const setLabel = useCallback((hex, label) => setLabels((l) => ({ ...l, [hex.toUpperCase()]: label })), []);

  const [favourites, setFavourites] = useState(() => {
    try {
      const f = JSON.parse(localStorage.getItem("sab-favs-v1") || "[]");
      return Array.isArray(f) ? f.map((x) => ({ ...x, label: usSpelling(x?.label) })) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("sab-favs-v1", JSON.stringify(favourites));
    } catch {
      /* storage unavailable */
    }
  }, [favourites]);
  const toggleFavourite = useCallback((hex, label) => {
    const h = hex.toUpperCase();
    setFavourites((f) => (f.some((x) => x.hex === h) ? f.filter((x) => x.hex !== h) : [...f, { hex: h, label }].slice(-MAX_FAVS)));
  }, []);

  const importFavourites = useCallback((list, replace) => {
    setFavourites((f) => {
      const base = replace ? [] : f.filter((x) => !list.some((n) => n.hex === x.hex));
      return [...base, ...list.map((x) => ({ ...x, label: usSpelling(x?.label) }))].slice(-MAX_FAVS);
    });
  }, []);
  const [favCompareOpen, setFavCompareOpen] = useState(false);

  const focusTarget = useCallback((sceneId, targetId) => {
    setCompare(false);
    setScene(sceneId);
    setTargets((t) => ({ ...t, [sceneId]: targetId }));
  }, []);

  const target = resolveTarget(scene, targets[scene]);

  useEffect(() => {
    try {
      localStorage.setItem("sab-sheens-v1", JSON.stringify(sheenState));
    } catch {
      /* ignore */
    }
  }, [sheenState]);

  const setSheen = useCallback(
    (id) => setSheenState((s) => ({ ...s, [scene]: { ...s[scene], ...Object.fromEntries(target.surfaces.map((k) => [k, id])) } })),
    [scene, target]
  );

  const selectTarget = useCallback((id) => setTargets((t) => ({ ...t, [scene]: id })), [scene]);

  const pickSurface = useCallback(
    (surface) => {
      const p = scenes[scene].pick;
      selectTarget(p === "piece" ? `piece:${surface}` : p[surface]);
    },
    [scene, selectTarget]
  );

  const apply = useCallback(
    (hex) => {
      setColors((c) => {
        const next = { ...c[scene] };
        target.surfaces.forEach((s) => (next[s] = hex));
        return { ...c, [scene]: next };
      });
    },
    [scene, target]
  );

  const applyCombo = useCallback((combo) => {
    const k = scenes.kitchen.targets;
    const set = (ids, hex) => Object.fromEntries(ids.map((s) => [s, hex]));
    setColors((c) => ({
      ...c,
      kitchen: { ...c.kitchen, ...set(k.find((t) => t.id === "walls").surfaces, combo.walls), ...set(k.find((t) => t.id === "cabinets").surfaces, combo.cabinets) },
    }));
  }, []);

  const reset = useCallback(() => {
    setColors((c) => ({ ...c, [scene]: { ...scenes[scene].original } }));
    setSheenState((s) => ({ ...s, [scene]: defaultSheens(scene) }));
  }, [scene]);

  const goTo = useCallback(
    (id) => {
      setCompare(false);
      const to = id === "exterior" ? lastExterior : id;
      if (exteriorModels.includes(to)) setLastExterior(to);
      const seen = sessionStorage.getItem("sab-orange-seen");
      if (exteriorModels.includes(to) && !exteriorModels.includes(scene) && !seen && !prefersReducedMotion()) {
        sessionStorage.setItem("sab-orange-seen", "1");
        setTransition({ to });
      } else {
        setScene(to);
      }
    },
    [scene, lastExterior]
  );

  const value = useMemo(
    () => ({ colors, labels, setLabel, sheens: sheenState, setSheen, favourites, toggleFavourite, importFavourites, favCompareOpen, setFavCompareOpen, focusTarget, scene, setScene, target, selectTarget, pickSurface, apply, applyCombo, reset, goTo, compare, setCompare, transition, setTransition }),
    [colors, labels, setLabel, sheenState, setSheen, favourites, toggleFavourite, importFavourites, favCompareOpen, focusTarget, scene, target, selectTarget, pickSurface, apply, applyCombo, reset, goTo, compare, transition]
  );

  return <PaintContext.Provider value={value}>{children}</PaintContext.Provider>;
};

export const usePaint = () => useContext(PaintContext);
